/**
 * The report download — wraps a rendered report in a self-contained HTML
 * document a reader can open, print or attach without the workspace.
 *
 * The demo downloaded the exported DOCX and PPTX from base64 blobs in its
 * data pack. Those blobs were dropped at extract time, so the port saves the
 * document the page rendered: its markup, the OEM logo resolved to an
 * absolute URL, and a small print stylesheet that stands in for the app's
 * tokens and utility classes. The stylesheet keys off the stable class names
 * the letterhead kit and the report scenes put on their elements.
 *
 * Pure string work — the scene reads the DOM and triggers the download; this
 * file never touches either.
 *
 * @see src/versions/v1/modules/reports/ReportActions.tsx
 */

/** Escape text for an HTML attribute or title. */
const escapeHtml = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/**
 * The stylesheet a downloaded report carries. Colours are the light theme's,
 * as literals on purpose: the file opens outside the app, where none of its
 * token sheets exist. `accent` is the brand's own accent so the rule under the
 * letterhead and the deck cover keep their colour.
 */
function exportStylesheet(accent: string): string {
  return `
:root{--accent:${accent};--ink-primary:#1a130f;--ink-secondary:#4a4038;--ink-tertiary:#7a6f66;--hairline:#e5e0d8;--hairline-strong:#cfc8bd;--surface-1:#fff;--surface-2:#faf8f4;--surface-3:#f1ede6;--color-zinc-900:#1a130f;--color-zinc-50:#f8f6f0}
*{box-sizing:border-box}
body{margin:0;padding:32px;background:#fff;color:var(--ink-primary);font:13px/1.5 "IBM Plex Sans",system-ui,-apple-system,"Segoe UI",sans-serif}
.report-doc{max-width:980px;margin:0 auto}
button{all:unset;color:var(--accent)}
.mono{font-family:"IBM Plex Mono",ui-monospace,monospace}
h2{font-size:18px;margin:0 0 2px}h3{font-size:13px;margin:14px 0 4px}h4{margin:0 0 6px;font-size:13px}
p{margin:6px 0}
table{border-collapse:collapse;width:100%;margin:6px 0 10px;font-size:11.5px}
th,td{border-bottom:1px solid var(--hairline);padding:4px 8px;text-align:left;vertical-align:top}
th{color:var(--ink-tertiary);font-weight:600;white-space:nowrap}
td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
.note{font-size:11.5px;color:var(--ink-tertiary)}
.letterhead{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding-bottom:10px;border-bottom:2px solid var(--accent);margin-bottom:12px}
.letterhead img{height:30px;width:auto}
.lh-right{text-align:right;font-size:11px;color:var(--ink-tertiary);line-height:1.4}.lh-right b{color:var(--ink-primary)}
.doccontrol{display:flex;gap:14px;flex-wrap:wrap;font:10.5px "IBM Plex Mono",monospace;color:var(--ink-tertiary);margin:-4px 0 12px}
.docfoot{border-top:1px solid var(--hairline);margin-top:16px;padding-top:6px;font-size:10px;color:var(--ink-tertiary);display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap}
.deckcover{background:var(--color-zinc-900);color:var(--color-zinc-50);border-radius:8px;padding:18px 20px 28px;margin-bottom:10px;position:relative}
.deckcover img{height:26px;width:auto}.deckcover h4{color:var(--color-zinc-50);font-size:20px;margin:14px 0 4px}
.deckcover .sub{font-size:12.5px;opacity:.9}.deckcover .meta{font-size:11px;opacity:.7;margin-top:6px}
.deckcover i{position:absolute;left:20px;bottom:14px;width:48px;height:3px;background:var(--accent)}
.slide{border:1px solid var(--hairline);border-radius:8px;padding:14px 16px;margin-bottom:10px;background:var(--surface-2);page-break-inside:avoid}
.kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
.kpi{border:1px solid var(--hairline);border-radius:8px;padding:8px 10px;background:#fff}
.band-pill{display:inline-block;border-radius:999px;padding:0 8px;font-size:11px;border:1px solid var(--hairline)}
.band-major,.band-vpoor{color:#9b1c1c}.band-poor{color:#9a5b00}.band-fair{color:#6b6200}.band-good{color:#1d6b3a}
svg{max-width:100%;height:auto}
@media print{body{padding:0}.slide{break-inside:avoid}}
`;
}

/**
 * A complete HTML file for one report.
 *
 * @param title   the document's `<title>`
 * @param body    the rendered report's outer HTML
 * @param accent  the brand accent as a hex colour
 * @param origin  the page origin, so root-relative `src` (the logo) resolves
 */
export function reportHtmlFile(title: string, body: string, accent: string, origin: string): string {
  // The file is always light: a letterhead or signature logo captured in the
  // dark theme is the reversed file, which would vanish on white paper. The
  // deck cover is dark in both themes and keeps its reversed logo.
  const resolved = body
    .replace(/(class="(?:lh-logo|mailsig)[^"]*"[^>]*>\s*<img[^>]*?src="[^"]*?)logo-reversed\.svg/g, '$1logo.svg')
    .replace(/(\ssrc=")\/(?!\/)/g, `$1${origin}/`);
  return (
    '<!doctype html>\n<html lang="en-AU">\n<head>\n<meta charset="utf-8">\n' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">\n' +
    `<title>${escapeHtml(title)}</title>\n<style>${exportStylesheet(accent)}</style>\n</head>\n` +
    `<body>\n${resolved}\n</body>\n</html>\n`
  );
}

/** The MIME type a download is saved with. */
export const DOWNLOAD_MIME = {
  html: 'text/html;charset=utf-8',
  eml: 'message/rfc822',
} as const;
